import {
  BusinessType,
  BusinessTypeNamingMapping,
  stringFormatter,
} from "@kivunova/kivufrontendcommon";

/** Human-readable labels for SNAKE_CASE enum values (fallback when no explicit map). */
export function formatEnumLabel(
  value: string | null | undefined,
  empty = "—",
): string {
  if (value == null || !String(value).trim()) return empty;
  const formatted = stringFormatter(String(value).trim());
  return formatted === "N/A" ? empty : formatted;
}

export function formatBusinessTypeLabel(
  value: string | null | undefined,
  empty = "—",
): string {
  if (value == null || !String(value).trim()) return empty;
  const key = String(value).trim().toUpperCase();
  const mapped =
    BusinessTypeNamingMapping[key as BusinessType] ??
    BusinessTypeNamingMapping[value as BusinessType];
  return mapped ?? formatEnumLabel(value, empty);
}

export const BUSINESS_TYPE_OPTIONS = Object.values(BusinessType).map(
  (value) => ({
    value,
    label: BusinessTypeNamingMapping[value],
  }),
);

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  COMPLETED: "Completed",
  SUCCESS: "Success",
  PENDING: "Pending",
  FAILED: "Failed",
  CANCELLED: "Cancelled",
  EXPIRED: "Expired",
};

export function formatPaymentStatusLabel(
  value: string | null | undefined,
  empty = "—",
): string {
  if (value == null || !String(value).trim()) return empty;
  const key = String(value).trim().toUpperCase();
  return PAYMENT_STATUS_LABELS[key] ?? formatEnumLabel(key, empty);
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  MTN_MOMO: "MTN MoMo",
  AIRTEL_MONEY: "Airtel Money",
  CREDIT_DEBIT: "Credit / debit card",
  PAYPAL: "PayPal",
  CARD: "Card",
  MOBILE_MONEY: "Mobile money",
  STRIPE: "Stripe",
  PAYSTACK: "Paystack",
  FLUTTERWAVE: "Flutterwave",
};

export function formatPaymentMethodLabel(
  value: string | null | undefined,
  empty = "—",
): string {
  if (value == null || !String(value).trim()) return empty;
  const key = String(value).trim().toUpperCase();
  return PAYMENT_METHOD_LABELS[key] ?? formatEnumLabel(key, empty);
}

const SUBSCRIPTION_STATUS_LABELS: Record<string, string> = {
  ACTIVE: "Active",
  PENDING: "Pending",
  PAUSED: "Paused",
  EXPIRED: "Expired",
  CANCELLED: "Cancelled",
  PAST_DUE: "Past due",
  TRIAL: "Trial",
};

export function formatSubscriptionStatusLabel(
  value: string | null | undefined,
  empty = "—",
): string {
  if (value == null || !String(value).trim()) return empty;
  const key = String(value).trim().toUpperCase();
  return SUBSCRIPTION_STATUS_LABELS[key] ?? formatEnumLabel(key, empty);
}

export const SUBSCRIPTION_STATUS_FILTER_OPTIONS = [
  { value: "", label: "All statuses" },
  ...Object.entries(SUBSCRIPTION_STATUS_LABELS).map(([value, label]) => ({
    value,
    label,
  })),
];

const COMPANY_STATUS_LABELS: Record<string, string> = {
  PENDING: "Pending",
  ACTIVE: "Active",
  SUSPENDED: "Suspended",
  DELETED: "Deleted",
};

export function formatCompanyStatusLabel(
  value: string | null | undefined,
  empty = "—",
): string {
  if (value == null || !String(value).trim()) return empty;
  const key = String(value).trim().toUpperCase();
  return COMPANY_STATUS_LABELS[key] ?? formatEnumLabel(key, empty);
}

const ACCOUNT_STATUS_LABELS: Record<string, string> = {
  ACTIVE: "Active",
  SUSPENDED: "Suspended",
  DELETED: "Deleted",
  PENDING: "Pending",
};

export function formatAccountStatusLabel(
  value: string | null | undefined,
  empty = "—",
): string {
  if (value == null || !String(value).trim()) return empty;
  const key = String(value).trim().toUpperCase();
  return ACCOUNT_STATUS_LABELS[key] ?? formatEnumLabel(key, empty);
}

/** Match filter text against raw enum value and formatted label. */
export function matchesEnumFilter(
  rawValue: string | null | undefined,
  filter: string,
  formatLabel: (value: string | null | undefined) => string,
): boolean {
  if (!filter.trim()) return true;
  const term = filter.trim().toLowerCase();
  const raw = String(rawValue ?? "").toLowerCase();
  const label = formatLabel(rawValue).toLowerCase();
  return raw.includes(term) || label.includes(term);
}
